import { useState, useEffect } from 'react';
import { imageCache, CacheStatsDisplay } from '../utils/imageCache';

export default function CacheStats() {
  const [stats, setStats] = useState<CacheStatsDisplay | null>(null);
  const [isOpen, setIsOpen] = useState(false);
  const [isClearing, setIsClearing] = useState(false);

  useEffect(() => {
    if (isOpen) {
      loadStats();
      // Refresh stats every 2 seconds when open
      const interval = setInterval(loadStats, 2000);
      return () => clearInterval(interval);
    }
  }, [isOpen]);

  async function loadStats() {
    const newStats = await imageCache.getStats();
    setStats(newStats);
  }

  async function handleClearCache() {
    if (!confirm('Clear all cached images? This cannot be undone.')) {
      return;
    }

    setIsClearing(true);
    try {
      await imageCache.clear();
      await loadStats();
      alert('Cache cleared successfully!');
    } catch (error) {
      console.error('Failed to clear cache:', error);
      alert('Failed to clear cache. Check console for details.');
    } finally {
      setIsClearing(false);
    }
  }

  async function handleCleanup() {
    try {
      await imageCache.cleanup();
      await loadStats();
      alert('Cleanup complete! Expired items removed.');
    } catch (error) {
      console.error('Failed to cleanup cache:', error);
      alert('Failed to cleanup cache. Check console for details.');
    }
  }

  if (!isOpen) {
    return (
      <button
        onClick={() => setIsOpen(true)}
        className="fixed bottom-4 right-4 bg-blue-600 text-white p-3 rounded-full shadow-lg hover:bg-blue-700 transition-colors z-40"
        title="Cache Statistics"
      >
        <svg
          className="w-6 h-6"
          fill="none"
          viewBox="0 0 24 24"
          stroke="currentColor"
          strokeWidth={2}
        >
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z"
          />
        </svg>
      </button>
    );
  }

  return (
    <div className="fixed bottom-4 right-4 bg-white rounded-lg shadow-2xl border border-gray-200 p-4 w-80 z-40">
      <div className="flex items-center justify-between mb-4">
        <h3 className="text-lg font-bold text-gray-900 flex items-center">
          <svg
            className="w-5 h-5 mr-2 text-blue-600"
            fill="none"
            viewBox="0 0 24 24"
            stroke="currentColor"
            strokeWidth={2}
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z"
            />
          </svg>
          Cache Stats
        </h3>
        <button
          onClick={() => setIsOpen(false)}
          className="text-gray-400 hover:text-gray-600"
        >
          <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M6 18L18 6M6 6l12 12"
            />
          </svg>
        </button>
      </div>

      {stats ? (
        <div className="space-y-3">
          {/* Cache Hit Rate */}
          <div className="bg-gradient-to-r from-green-50 to-blue-50 rounded-lg p-3 border border-green-200">
            <div className="text-xs font-semibold text-gray-600 mb-1">Hit Rate</div>
            <div className="flex items-baseline">
              <div className="text-3xl font-bold text-green-700">
                {stats.hitRate.toFixed(1)}%
              </div>
              <div className="ml-2 text-xs text-gray-500">
                ({stats.cacheHits} hits / {stats.cacheMisses} misses)
              </div>
            </div>
            <div className="mt-2 bg-gray-200 rounded-full h-2 overflow-hidden">
              <div
                className="bg-gradient-to-r from-green-500 to-blue-500 h-full transition-all duration-500"
                style={{ width: `${Math.min(100, stats.hitRate)}%` }}
              />
            </div>
          </div>

          {/* Cache Size */}
          <div className="bg-gray-50 rounded-lg p-3 border border-gray-200">
            <div className="flex justify-between items-center">
              <div>
                <div className="text-xs font-semibold text-gray-600">Cache Size</div>
                <div className="text-2xl font-bold text-gray-900">{stats.formattedSize}</div>
              </div>
              <div className="text-right">
                <div className="text-xs font-semibold text-gray-600">Items</div>
                <div className="text-2xl font-bold text-blue-600">{stats.itemCount}</div>
              </div>
            </div>
          </div>

          {/* Detailed Stats */}
          <div className="text-xs space-y-1.5 text-gray-600 bg-gray-50 rounded-lg p-2 border border-gray-200">
            <div className="flex justify-between">
              <span>Total Requests:</span>
              <span className="font-semibold">{stats.cacheHits + stats.cacheMisses}</span>
            </div>
            <div className="flex justify-between">
              <span>Cache Hits:</span>
              <span className="font-semibold text-green-600">{stats.cacheHits}</span>
            </div>
            <div className="flex justify-between">
              <span>Cache Misses:</span>
              <span className="font-semibold text-red-600">{stats.cacheMisses}</span>
            </div>
          </div>

          {/* Actions */}
          <div className="flex gap-2 pt-2">
            <button
              onClick={handleCleanup}
              className="flex-1 px-3 py-2 text-sm bg-yellow-100 text-yellow-800 rounded-lg hover:bg-yellow-200 transition-colors font-medium"
            >
              🧹 Cleanup
            </button>
            <button
              onClick={handleClearCache}
              disabled={isClearing}
              className="flex-1 px-3 py-2 text-sm bg-red-100 text-red-800 rounded-lg hover:bg-red-200 transition-colors font-medium disabled:opacity-50"
            >
              {isClearing ? 'Clearing...' : '🗑️ Clear All'}
            </button>
          </div>

          {/* Info */}
          <div className="text-xs text-gray-500 italic pt-2 border-t border-gray-200">
            <p>💡 Images are cached locally for 7 days</p>
            <p>💡 Max cache size: 500 MB</p>
            <p>💡 Open DevTools Console to see cache logs</p>
          </div>
        </div>
      ) : (
        <div className="flex items-center justify-center h-32">
          <div className="animate-spin rounded-full h-8 w-8 border-2 border-gray-200 border-t-blue-600"></div>
        </div>
      )}
    </div>
  );
}

